import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import {
  clinicBankAccountApi,
  clinicBankAccountQueryKeys,
  type BankAccount,
} from "../../shared/api/clinicBankAccount";
import { apiError } from "../../shared/api/client";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Tài khoản phòng khám nhận viện phí (#19010).
 *
 * <p>Ba ô, không có ô thứ tư. Bệnh nhân quét mã và chuyển THẲNG vào tài khoản này — Addy dựng mã
 * chứ không cầm tiền, không đọc sao kê, và phòng khám không phải đăng ký dịch vụ nào khác.
 *
 * <p>Ngân hàng là Ô CHỌN chứ không phải ô gõ. Gõ tay thì "VCB" hay "Vietcom bank" vẫn lưu được,
 * mã QR vẫn vẽ ra, và sai chỉ lộ khi bệnh nhân đứng quét — lúc đó không ai biết tại sao.
 */
export function BankAccountSettingsPage() {
  const qc = useQueryClient();
  const banks = useQuery({
    queryKey: clinicBankAccountQueryKeys.banks(),
    queryFn: clinicBankAccountApi.banks,
    staleTime: 60 * 60 * 1000, // danh bạ ngân hàng không đổi trong một phiên làm việc
  });
  const accounts = useQuery({
    queryKey: clinicBankAccountQueryKeys.list(),
    queryFn: clinicBankAccountApi.list,
  });

  const active = accounts.data?.find((a) => a.active) ?? null;

  const [editing, setEditing] = useState<BankAccount | null>(null);
  const [bankBin, setBankBin] = useState("");
  const [accountNo, setAccountNo] = useState("");
  const [accountName, setAccountName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  // Không nhớ giữa các lần lưu, và cố ý: mỗi lần đổi tài khoản nhận tiền là một lần phải khẳng
  // định lại. Một ô tích còn nguyên từ lần trước không phải lời khẳng định của lần này.
  const [attested, setAttested] = useState(false);

  // Mở màn hình lần đầu: điền sẵn tài khoản đang dùng, để sửa một chữ số không phải gõ lại cả ba ô.
  useEffect(() => {
    if (active && !editing) {
      setEditing(active);
      setBankBin(active.bankBin);
      setAccountNo(active.accountNo);
      setAccountName(active.accountName);
    }
  }, [active, editing]);

  const bank = useMemo(
    () => banks.data?.find((b) => b.bin === bankBin) ?? null,
    [banks.data, bankBin],
  );

  // Xem trước dựng NGAY TRÊN MÁY từ những gì đang gõ, không đợi lưu và không gọi server: phòng khám
  // phải thấy được mã trước khi bấm lưu, vì sau khi lưu thì người quét là bệnh nhân.
  const previewReady = Boolean(bank && /^[A-Za-z0-9]{4,32}$/.test(accountNo.trim()));

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        bankBin,
        accountNo: accountNo.trim(),
        accountName: accountName.trim(),
      };
      if (editing) {
        await clinicBankAccountApi.update(editing.id, body);
        await clinicBankAccountApi.activate(editing.id, attested);
      } else {
        const id = await clinicBankAccountApi.create(body);
        await clinicBankAccountApi.activate(id, attested);
      }
    },
    onSuccess: () => {
      setError(null);
      setMsg(
        "Đã lưu. Mã QR trên hoá đơn từ giờ trỏ vào tài khoản này, và mọi mã đã phát hành trước đó"
          + " thôi hiệu lực.",
      );
      setAttested(false);
      qc.invalidateQueries({ queryKey: clinicBankAccountQueryKeys.list() });
    },
    onError: (e) => {
      setMsg(null);
      setError(apiError(e).detail);
    },
  });

  const valid =
    previewReady && accountName.trim().length > 0 && attested && !save.isPending;

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title="Tài khoản nhận tiền"
        description="Bệnh nhân quét mã và chuyển thẳng vào tài khoản này. Addy chỉ tạo mã QR — tiền không đi qua Addy."
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <Card>
          <CardContent className="space-y-4 p-5">
            <div className="space-y-1.5">
              <Label htmlFor="bank">Ngân hàng</Label>
              <Select value={bankBin} onValueChange={setBankBin}>
                <SelectTrigger id="bank">
                  <SelectValue placeholder="Chọn ngân hàng" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {(banks.data ?? []).map((b) => (
                    <SelectItem key={b.bin} value={b.bin}>
                      {b.shortName} — {b.fullName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Chỉ hiện các ngân hàng nhận được chuyển khoản nhanh. Ngân hàng không có trong danh
                sách thì mã QR vẽ ra được nhưng không ai chuyển vào được.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="acc">Số tài khoản</Label>
                <Input
                  id="acc"
                  value={accountNo}
                  onChange={(e) => setAccountNo(e.target.value)}
                  placeholder="37388437"
                  inputMode="numeric"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="accName">Tên chủ tài khoản</Label>
                <Input
                  id="accName"
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  placeholder="PHONG KHAM ABC"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Tên chủ tài khoản không đi vào mã QR. Nó hiện cạnh mã để bệnh nhân đối chiếu trước khi
              bấm chuyển — và để phòng khám tự phát hiện nếu gõ nhầm số tài khoản của người khác.
            </p>

            <label className="flex items-start gap-2.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm">
              <input
                type="checkbox"
                className="mt-0.5 size-4 shrink-0"
                checked={attested}
                onChange={(e) => setAttested(e.target.checked)}
              />
              <span className="text-amber-900">
                Tôi xác nhận tài khoản trên <strong>thuộc về phòng khám</strong>. Mọi khoản viện phí
                bệnh nhân chuyển khoản từ giờ sẽ vào đúng tài khoản này. Addy không kiểm tra được
                điều đó thay phòng khám và không giữ hộ đồng nào — chuyển nhầm thì không rút lại
                được.
              </span>
            </label>

            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </p>
            )}
            {msg && (
              <p className="rounded-md bg-primary/10 px-3 py-2 text-sm text-primary">{msg}</p>
            )}

            <div className="flex items-center gap-3">
              <Button onClick={() => save.mutate()} disabled={!valid}>
                {save.isPending ? "Đang lưu…" : "Lưu tài khoản"}
              </Button>
              {active && (
                <span className="text-xs text-muted-foreground">
                  Đang dùng: {active.bankShortName} · {active.accountNo}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-3 p-5">
            <h2 className="text-sm font-semibold text-foreground">Xem trước mã QR</h2>
            {previewReady ? (
              <BankQrPreview
                bankBin={bankBin}
                accountNo={accountNo.trim()}
                bankShortName={bank?.shortName ?? ""}
                accountName={accountName.trim()}
              />
            ) : (
              <p className="text-sm text-muted-foreground">
                Chọn ngân hàng và nhập số tài khoản để xem mã.
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Hãy tự quét thử bằng app ngân hàng của mình. Máy quét phải hiện đúng tên chủ tài khoản
              trên — đó là cách duy nhất chắc chắn số tài khoản gõ không sai.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/**
 * Mã xem trước KHÔNG có số tiền: nó chỉ để phòng khám kiểm tra đúng tài khoản chưa. Mã có số tiền
 * là mã dùng một lần, dựng theo từng hoá đơn ở màn thu tiền.
 */
function BankQrPreview({
  bankBin,
  accountNo,
  bankShortName,
  accountName,
}: {
  bankBin: string;
  accountNo: string;
  bankShortName: string;
  accountName: string;
}) {
  // Dựng tại chỗ, không gọi server: gọi server thì phải LƯU trước mới xem được mã, tức là bắt
  // phòng khám cam kết một tài khoản trước khi nhìn thấy nó đúng hay sai.
  const payload = buildPreviewPayload(bankBin, accountNo);
  return (
    <div className="space-y-2">
      <div className="flex justify-center rounded-md bg-white p-3">
        <QRCodeSVG value={payload} size={188} level="M" />
      </div>
      <dl className="space-y-0.5 text-sm">
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">Ngân hàng</dt>
          <dd className="font-medium">{bankShortName}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">Số tài khoản</dt>
          <dd className="font-medium tabular-nums">{accountNo}</dd>
        </div>
        {accountName && (
          <div className="flex justify-between gap-2">
            <dt className="text-muted-foreground">Chủ tài khoản</dt>
            <dd className="max-w-[60%] truncate font-medium" title={accountName}>
              {accountName}
            </dd>
          </div>
        )}
      </dl>
    </div>
  );
}

/**
 * Bản dựng chuỗi VietQR cho XEM TRƯỚC, chạy trên máy để phòng khám thấy mã trước khi lưu.
 *
 * <p>Bản dùng thật nằm ở server ({@code VietQr.java}) và được khoá bằng một chuỗi tham chiếu do
 * chính VietQR sinh ra. Hai bản này phải cho cùng kết quả; test `bankQr.test.ts` giữ điều đó.
 */
export function buildPreviewPayload(bankBin: string, accountNo: string): string {
  const tlv = (id: string, value: string) =>
    id + String(value.length).padStart(2, "0") + value;
  const beneficiary = tlv("00", bankBin) + tlv("01", accountNo);
  const merchant =
    tlv("00", "A000000727") + tlv("01", beneficiary) + tlv("02", "QRIBFTTA");
  const body =
    tlv("00", "01") + tlv("01", "11") + tlv("38", merchant) + tlv("53", "704") + tlv("58", "VN");
  const withTrailer = body + "6304";
  return withTrailer + crc16(withTrailer);
}

/** CRC-16/CCITT-FALSE — phải khớp bản Java, nếu không mã xem trước khác mã thật. */
function crc16(s: string): string {
  let crc = 0xffff;
  for (const byte of new TextEncoder().encode(s)) {
    crc ^= byte << 8;
    for (let i = 0; i < 8; i += 1) {
      crc = (crc & 0x8000) !== 0 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff;
    }
  }
  return crc.toString(16).toUpperCase().padStart(4, "0");
}
